import { useCallback, useMemo } from "react";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addJointAccount,
  bulkImportClients,
  createClient,
  deleteClient,
  getAllClients,
  getClientAssignedEmployee,
  getClientById,
  getClientSetupDetails,
  sendHolderApprovalLink,
  updateClient,
  type BulkImportLeadRow,
} from "../../api/clients";
import { Client, ClientsFilter, CustomQueryOptions } from "../../types";

export const clientsQueryOptions = (
  {
    page = 1,
    pageSize = 20,
    search = "",
    filters,
    sort,
  }: {
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: ClientsFilter;
    sort?: Array<{ id: string; desc: boolean }>;
  },
  options?: { enabled?: boolean }
) => {
  return queryOptions({
    queryKey: ["clients", { page, pageSize, ...filters, search, sort }],
    queryFn: () => getAllClients(page, pageSize, search, filters, sort),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
};

export const useClients = (
  variables: {
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: ClientsFilter;
    sort?: Array<{ id: string; desc: boolean }>;
  },
  options?: { enabled?: boolean }
) => useQuery(clientsQueryOptions(variables, options));

export function useInvalidateClients() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["clients"] });
  }, [queryClient]);
}

export const useClientsInfiniteQuery = (
  search: string = "",
  filters?: ClientsFilter,
  sort?: Array<{ id: string; desc: boolean }>,
  options?: { enabled?: boolean; pageSize?: number }
) => {
  const pageSize = options?.pageSize ?? 50;
  const response = useInfiniteQuery({
    queryKey: ["infinite-clients", { search, ...filters, sort, pageSize }],
    queryFn: (context) =>
      getAllClients(context.pageParam, pageSize, search, filters, sort),
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    initialPageParam: 1,
    select: (queryResponse) => queryResponse.pages,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });

  const clients = useMemo(
    () => response.data?.flatMap((d) => d.results),
    [response.data]
  );

  return {
    ...response,
    clients,
  };
};

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["infinite-clients"] });
    },
  });
}

export function useAddJointAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addJointAccount,
    onSuccess: (jointClient, sourceClientId) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["infinite-clients"] });
      queryClient.invalidateQueries({
        queryKey: ["client", { id: Number(sourceClientId) }],
      });
      queryClient.invalidateQueries({
        queryKey: ["client", { id: jointClient.id }],
      });
    },
  });
}

export function useSendHolderApprovalLink() {
  return useMutation({
    mutationFn: ({
      clientId,
      ...payload
    }: {
      clientId: number | string;
      investment_account_guid: string;
      profile_id: string;
    }) => sendHolderApprovalLink(clientId, payload),
  });
}

export function useBulkImportClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: BulkImportLeadRow[]) => bulkImportClients({ rows }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export const clientDetailsQueryOptions = (
  id: number,
  enabled: boolean = true
) =>
  queryOptions({
    queryKey: ["client", { id }],
    queryFn: () => getClientById(id),
    enabled: !!id && Number(id) > 0 && enabled,
  });

export function useClient(id: number, enabled?: boolean) {
  return useQuery(clientDetailsQueryOptions(id, enabled));
}

export function useClientsByIds(clientIds: number[]) {
  const uniqueIds = useMemo(
    () => Array.from(new Set(clientIds)).filter((id) => id > 0),
    [clientIds]
  );

  const clientQueries = useQueries({
    queries: uniqueIds.map((id) => clientDetailsQueryOptions(id)),
  });

  const clients = useMemo(
    () =>
      clientQueries
        .map((query) => query.data)
        .filter((client): client is Client => !!client),
    [clientQueries]
  );

  const isLoading = clientQueries.some((query) => query.isLoading);

  return { clients, isLoading };
}

export function useInvalidateClient(id: number) {
  const queryClient = useQueryClient();

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["client", { id }] }),
    [queryClient, id]
  );
}

export const clientAssignedEmployeeQueryOptions = (
  clientId: number,
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: ["client-assigned-employee", { clientId }],
    queryFn: () => getClientAssignedEmployee(clientId),
    enabled: !!clientId && Number(clientId) > 0 && (options?.enabled ?? true),
    select: (data) => (Object.keys(data).length ? data : null),
  });

export function useClientAssignedEmployee(
  clientId: number,
  options?: { enabled?: boolean }
) {
  return useQuery(clientAssignedEmployeeQueryOptions(clientId, options));
}

export function useUpdateClient(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["client", { id }],
    mutationFn: (data: Partial<Client>) => updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", { id }] });
    },
  });
}

export function useDeleteClient(id: number) {
  return useMutation({ mutationFn: () => deleteClient(id) });
}

export function useClientSetupDetails(code: string) {
  return useQuery({
    queryKey: ["client-setup-details", { code }],
    queryFn: () => getClientSetupDetails(code),
  });
}

export function usePollClient(
  variables: { id: string | number },
  options?: CustomQueryOptions<typeof getClientById>
) {
  return useQuery({
    ...options,
    queryKey: ["poll-client", { id: variables.id }],
    queryFn: () => getClientById(variables.id),
    refetchInterval: options?.refetchInterval ?? 5000,
  });
}
